import {
  createCollection,
  localStorageCollectionOptions,
  useLiveQuery,
} from "@tanstack/react-db";
import { z } from "zod";
import { PREFIX, SINGLETON_KEY } from "./storage-keys";

export const createExerciseStore = <TAttempt, TWordData>(
  name: string,
  data: readonly TWordData[],
  attemptSchema: z.ZodType<TAttempt>,
  computeScore: (attempts: TAttempt[]) => number | "IN_PROGRESS",
) => {
  const storageKey = `${PREFIX}-${name}`;

  const schema = z.object({
    sessions: z
      .object({
        sessionStartedAt: z.number(),
        sessionEndedAt: z.number().optional(),
        results: z
          .object({
            attempts: attemptSchema.array(),
            resultConfirmed: z.boolean(),
            score: z.number().optional(),
          })
          .array(),
      })
      .array(),
  });

  const collection = createCollection(
    localStorageCollectionOptions({
      id: `${name}-progress`,
      storageKey,
      getKey: () => SINGLETON_KEY,
      schema,
    }),
  );

  const getCurrent = (
    progress?: z.infer<typeof schema>,
  ):
    | {
        kind: "SHOW_SUMMARY";
        data: {
          maxScore: number;
          sessions: { startedAt: number; endedAt: number; score: number }[];
        };
      }
    | {
        kind: "SHOW_CURRENT_WORD";
        data: {
          word: TWordData;
          results: {
            attempts: TAttempt[];
            resultConfirmed: boolean;
            score?: number | undefined;
          };
          progress: {
            current: number;
            total: number;
          };
        };
      } => {
    if (!progress) {
      return {
        kind: "SHOW_SUMMARY",
        data: {
          maxScore: data.length * 100,
          sessions: [],
        },
      };
    }

    const currentSession = progress.sessions[progress.sessions.length - 1];
    if (!currentSession) {
      throw new Error("UNEXPECTED STATE : Progress store with no session");
    }

    const results = currentSession.results;

    const indexToUse = results[results.length - 1]?.resultConfirmed
      ? results.length
      : Math.max(0, results.length - 1);

    if (indexToUse === data.length) {
      const sessions = progress.sessions
        .filter(
          (s): s is typeof s & { sessionEndedAt: number } =>
            s.sessionEndedAt !== undefined,
        )
        .map((s) => ({
          startedAt: s.sessionStartedAt,
          endedAt: s.sessionEndedAt,
          score: s.results.reduce((t, r) => t + (r.score ?? 0), 0),
        }));

      return {
        kind: "SHOW_SUMMARY",
        data: {
          maxScore: data.length * 100,
          sessions,
        },
      };
    }

    const word = data[indexToUse];
    if (!word) {
      throw new Error("UNEXPECTED STATE : Word data missing for current index");
    }
    const wordResults = results[indexToUse] || {
      attempts: [],
      resultConfirmed: false,
    };
    return {
      kind: "SHOW_CURRENT_WORD",
      data: {
        word,
        results: wordResults,
        progress: {
          current: indexToUse,
          total: data.length,
        },
      },
    };
  };

  const useCurrent = () => {
    const { data: queryData } = useLiveQuery((q) =>
      q.from({ [name]: collection }),
    );
    return getCurrent(queryData[0]);
  };

  const useGlobalState = () => {
    const { data: queryData } = useLiveQuery((q) =>
      q.from({ [name]: collection }),
    );
    const progress = queryData[0];
    if (!progress) return null;

    const session = progress.sessions[progress.sessions.length - 1];
    if (!session) return null;

    const currentProgress = session.results.filter(
      (r) => r.resultConfirmed,
    ).length;
    const score = session.results.reduce((t, r) => t + (r.score ?? 0), 0);
    const isFinished = currentProgress >= data.length;
    const finishedSessions = progress.sessions.filter(
      (s) => s.sessionEndedAt !== undefined,
    );
    const wasEverFinished = finishedSessions.length > 0;
    const sessionCount = finishedSessions.length;
    const bestScore = Math.max(
      0,
      ...finishedSessions.map((s) =>
        s.results.reduce((t, r) => t + (r.score ?? 0), 0),
      ),
    );
    const lastFinishedSession = finishedSessions[finishedSessions.length - 1];
    const lastScore = lastFinishedSession
      ? lastFinishedSession.results.reduce((t, r) => t + (r.score ?? 0), 0)
      : 0;

    return {
      progress: currentProgress,
      score,
      total: data.length,
      isFinished,
      wasEverFinished,
      sessionCount,
      bestScore,
      lastScore,
    };
  };

  const initialize = () => {
    const existing = collection.state.get(SINGLETON_KEY);
    if (existing) return;
    collection.insert({
      sessions: [{ sessionStartedAt: Date.now(), results: [] }],
    });
  };

  const addAttempt = (attempt: TAttempt) => {
    const current = getCurrent(collection.state.get(SINGLETON_KEY));
    if (current.kind !== "SHOW_CURRENT_WORD") {
      throw new Error(
        "Cannot add attempt when there is no current word being shown",
      );
    }

    const wordIndex = current.data.progress.current;

    collection.update(SINGLETON_KEY, (draft) => {
      const session = draft.sessions[draft.sessions.length - 1];
      if (!session) {
        throw new Error("UNEXPECTED STATE : No active session found");
      }

      if (session.results.length === wordIndex) {
        const newAttempts = [attempt];
        const score = computeScore(newAttempts);
        session.results.push({
          attempts: newAttempts,
          resultConfirmed: false,
          score: score === "IN_PROGRESS" ? undefined : score,
        });
        return;
      }

      if (session.results.length <= wordIndex) {
        throw new Error(
          `UNEXPECTED STATE : Results array too short - expected > ${wordIndex}, got ${session.results.length}`,
        );
      }

      const wordResult = session.results[wordIndex];
      if (!wordResult) {
        throw new Error("UNEXPECTED STATE : Word result missing");
      }
      wordResult.attempts.push(attempt);

      const score = computeScore(wordResult.attempts as TAttempt[]);
      if (score !== "IN_PROGRESS") {
        wordResult.score = score;
      }
    });
  };

  const confirmResult = () => {
    const current = getCurrent(collection.state.get(SINGLETON_KEY));
    if (current.kind !== "SHOW_CURRENT_WORD") {
      throw new Error("Cannot confirm result when there is no current word");
    }

    if (current.data.results.score === undefined) {
      throw new Error("Cannot confirm result without a score");
    }

    const wordIndex = current.data.progress.current;
    const isLast = wordIndex === data.length - 1;

    collection.update(SINGLETON_KEY, (draft) => {
      const session = draft.sessions[draft.sessions.length - 1];
      if (!session) {
        throw new Error("UNEXPECTED STATE : No active session found");
      }

      const wordResult = session.results[wordIndex];
      if (!wordResult) {
        throw new Error("UNEXPECTED STATE : Word result missing");
      }
      wordResult.resultConfirmed = true;

      if (isLast) {
        session.sessionEndedAt = Date.now();
      }
    });

    return { isLast };
  };

  const useSessionStatus = () => {
    const { data: queryData } = useLiveQuery((q) =>
      q.from({ [name]: collection }),
    );
    const progress = queryData[0];
    if (!progress || progress.sessions.length === 0) return "NO_SESSIONS";
    const session = progress.sessions[progress.sessions.length - 1];
    if (!session) return "NO_SESSIONS";
    if (session.sessionEndedAt !== undefined) return "FINISHED";
    return "IN_PROGRESS";
  };

  const useCurrentWordIndex = () => {
    const { data: queryData } = useLiveQuery((q) =>
      q.from({ [name]: collection }),
    );
    const progress = queryData[0];
    if (!progress) return 0;
    const session = progress.sessions[progress.sessions.length - 1];
    if (!session) return 0;
    const results = session.results;
    const idx = results[results.length - 1]?.resultConfirmed
      ? results.length
      : Math.max(0, results.length - 1);
    return idx;
  };

  const useSummaryData = () => {
    const { data: queryData } = useLiveQuery((q) =>
      q.from({ [name]: collection }),
    );
    const progress = queryData[0];
    if (!progress) return null;
    const finishedSessions = progress.sessions
      .filter(
        (s): s is typeof s & { sessionEndedAt: number } =>
          s.sessionEndedAt !== undefined,
      )
      .map((s) => ({
        startedAt: s.sessionStartedAt,
        endedAt: s.sessionEndedAt,
        score: s.results.reduce((t, r) => t + (r.score ?? 0), 0),
      }));
    return {
      maxScore: data.length * 100,
      sessions: finishedSessions,
    };
  };

  const skipRemaining = () => {
    collection.update(SINGLETON_KEY, (draft) => {
      const session = draft.sessions[draft.sessions.length - 1];
      if (!session) {
        throw new Error("UNEXPECTED STATE : No active session found");
      }

      const currentIndex =
        session.results.length > 0
          ? session.results[session.results.length - 1]?.resultConfirmed
            ? session.results.length
            : session.results.length - 1
          : 0;

      for (let i = currentIndex; i < data.length; i++) {
        const existing = session.results[i];
        if (!existing) {
          session.results.push({
            attempts: [],
            resultConfirmed: true,
            score: 0,
          });
          continue;
        }

        if (!existing.resultConfirmed) {
          const score = computeScore(existing.attempts as TAttempt[]);
          existing.score = score === "IN_PROGRESS" ? 0 : score;
          existing.resultConfirmed = true;
        }
      }

      session.sessionEndedAt = Date.now();
    });
  };

  const startNewSession = () => {
    const current = getCurrent(collection.state.get(SINGLETON_KEY));
    if (current.kind !== "SHOW_SUMMARY") {
      return;
    }

    collection.update(SINGLETON_KEY, (draft) => {
      draft.sessions.push({ sessionStartedAt: Date.now(), results: [] });
    });
  };

  return {
    storageKey,
    schema,
    useCurrent,
    useGlobalState,
    useSessionStatus,
    useCurrentWordIndex,
    useSummaryData,
    initialize,
    addAttempt,
    confirmResult,
    startNewSession,
    skipRemaining,
  };
};
